import { useRef, useState, useEffect, useMemo } from 'react';
import { TheirInfluenceFragment } from '@graphqlTypes';
import { getHighlightMarkup } from '@/core/utils';
import { Container, HeadlineAnim, Image, ImageAttribution, RevealWrapper } from '@components';
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView, motion } from 'motion/react';
import { AnimMode, AnimVals } from '@/utils/animUtils';

import InducteeInfluenceName from './Name';
import SvgLinesMobile from "@/assets/svg/inductee-influences-lines_mobile.svg";

import styles from './InducteeInfluence.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

type InducteeInfluenceFragment = TheirInfluenceFragment['inducteeData']['inducteeInfluence'];

type Influence =
  InducteeInfluenceFragment['influences'][number] |
  InducteeInfluenceFragment['influenced'][number];

type InducteeInfluenceProps = {
  inducteeInfluence: InducteeInfluenceFragment;
  inducteeName?: string;
};

export default function InducteeInfluence({ inducteeInfluence, inducteeName }: InducteeInfluenceProps) {
  const { labels } = usePageWrapperData();
  const { headline, body, image, influences, influenced, imageAttribution } = inducteeInfluence;
  const { influencedLabel, influencesLabel } = labels || {};

  const headlineMarkup = getHighlightMarkup(headline);

  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const getInfluenceData = (influence: Influence) => {
    const { type, name, inductee } = influence;
    let influenceName = name;
    let url;
    if (inductee && 'title' in inductee.node && type[0] === 'inductee') {
      influenceName = inductee.node.title;
      url = inductee.node.uri;
    }
    return { name: influenceName, url };
  };

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;

  const isRevealEnabled = AnimMode !== 'none';
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // Memoize motion props to ensure consistent SSR/CSR
  const imageMotionProps = useMemo(() => ({
    initial: { opacity: 0, scale: 0 },
    animate: isInView && isRevealEnabled ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 },
    transition: { delay: REVEAL.sm.delay, duration: REVEAL.lg.duration, ease: REVEAL.lg.ease }
  }), [isInView, isRevealEnabled, REVEAL]);

  const nameMotionProps = useMemo(() => ({
    initial: { opacity: 0 },
    animate: isInView && isRevealEnabled ? { opacity: 1 } : { opacity: 0 },
    transition: { delay: REVEAL.sm.delay + 0.2, duration: REVEAL.lg.duration, ease: REVEAL.lg.ease }
  }), [isInView, isRevealEnabled, REVEAL]);

  // --------------------

  return (
    <section className={cx("inductee-influence", { 'is-in-view': isInView, 'is-reveal-disabled': !isRevealEnabled })} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx("content")}>
          <div className={cx(["content__text"])}>
            <HeadlineAnim
              tag="h3"
              text={headlineMarkup}
              className={cx(["title-uc-xxxxl", "headline"])}
              isCenterAlign={true}
              isRevealEnabled={false}
              isInView={isInView}
            />
            { body && (
              <p className={cx(["text-m", "body"])}>{body}</p>
            )}
          </div>
          <div className={cx(["content__imagery"])}>

            {/* Left: List: Influences */}
            <div className={cx(["left", "bands", "influences"])}>
              <h5 className={cx(["title-uc-xs", "bands__title"])}>{influencesLabel}</h5>
              { influences.map((influence, index) => {
                const { name, url } = getInfluenceData(influence);
                return (
                  <InducteeInfluenceName
                    key={index}
                    index={index}
                    count={influences.length}
                    target={imageRef.current}
                    imageLoaded={imageLoaded}
                    name={name}
                    url={url}
                  />
                );
              })}
            </div>

            {/* Center: Inductee */}
            <div className={cx(["center", "inductee"])}>
              <motion.div
                className={cx("inductee__image-wrapper")}
                {...imageMotionProps}
              >
                <Image
                  image={image}
                  ref={imageRef}
                  onLoad={() => setImageLoaded(true)}
                />
                {imageAttribution && (
                  <ImageAttribution text={imageAttribution} position="center" className={cx("influence-attribution")} />
                )}
              </motion.div>
              <motion.div
                className={cx("inductee__name")}
                {...nameMotionProps}
              >
                <p className={cx("title-uc-xs")}>{inducteeName}</p>
              </motion.div>
              <div className={cx("flatlines")}>
                <SvgLinesMobile className="lines__mobile" />
              </div>
            </div>

            {/* Right: List: Influenced */}
            <div className={cx(["right", "bands", "influenced"])}>
              <h5 className={cx(["title-uc-xs", "bands__title"])}>{influencedLabel}</h5>
              { influenced.map((influence, index) => {
                const { name, url } = getInfluenceData(influence);
                return (
                  <InducteeInfluenceName
                    key={index}
                    index={index}
                    position='right'
                    count={influenced.length}
                    target={imageRef.current}
                    imageLoaded={imageLoaded}
                    name={name}
                    url={url}
                  />
                );
              })}
            </div>

          </div>
        </Container>
      </RevealWrapper>
    </section>
  );

}
